import { AnalysisResult } from '../types/analysis';

/**
 * Sample analysis shown at /sample-report ("View Sample Report" on the
 * landing page). Fictional startup; the structure exactly mirrors a real
 * analysis payload so the report renders identically to the real thing.
 */
export const SAMPLE_ANALYSIS: AnalysisResult = {
  analysis_timestamp: '2026-05-04T16:20:00Z',
  model_used: 'claude',
  original_filename: 'GoosePatrol_SeriesSeed_Deck.pdf',
  file_size_bytes: 4815162,
  overall_score: 6.4,
  score_percentile: 71,
  overall_analysis:
    "GoosePatrol ('Uber for goose removal — but make it drones') is a genuinely entertaining deck hiding a surprisingly real business. Golf courses, airports, and corporate campuses collectively spend serious money on goose mitigation, and the team's autonomous drone-hazing system has actual paying pilots. The deck's strengths are traction and a memorable narrative; its weaknesses are a hand-wavy TAM slide ('every goose on Earth'), financial projections that hockey-stick on slide 14 with no stated assumptions, and a competitive analysis that dismisses incumbents (trained border collies) a little too quickly. Investor-ready after a serious pass on the numbers.",
  analysis:
    "GoosePatrol ('Uber for goose removal — but make it drones') is a genuinely entertaining deck hiding a surprisingly real business. Golf courses, airports, and corporate campuses collectively spend serious money on goose mitigation, and the team's autonomous drone-hazing system has actual paying pilots. The deck's strengths are traction and a memorable narrative; its weaknesses are a hand-wavy TAM slide ('every goose on Earth'), financial projections that hockey-stick on slide 14 with no stated assumptions, and a competitive analysis that dismisses incumbents (trained border collies) a little too quickly. Investor-ready after a serious pass on the numbers.",
  category_scores: {
    title_company_intro: {
      score: 8,
      maxScore: 10,
      displayName: 'Title & Company Intro',
      reasoning:
        "Slide 1 is instantly memorable: company name, a drone silhouette chasing a goose, and the tagline 'The geese don't pay rent.' You know exactly what this company does in four seconds — most decks can't say that.",
    } as any,
    problem_opportunity: {
      score: 7.5,
      maxScore: 10,
      displayName: 'Problem / Opportunity',
      reasoning:
        'Strong, specific problem framing: $0.8B spent annually on goose damage and mitigation in North America, with vivid examples (a Cleveland office park reportedly held hostage by a flock named "The Boys"). Would score higher with a citation for the headline number.',
    } as any,
    value_proposition: {
      score: 7,
      maxScore: 10,
      displayName: 'Value Proposition',
      reasoning:
        "Clear before/after: groundskeepers currently chase geese with brooms; GoosePatrol's drones haze them humanely on a schedule, with a compliance dashboard. The 'wildlife-relocation-as-a-service' subscription framing is compelling, though the humane-certification claim needs documentation.",
    } as any,
    technology_magic: {
      score: 6.5,
      maxScore: 10,
      displayName: 'Technology / Magic',
      reasoning:
        'The goose-detection computer vision model ("GooseNet") and autonomous patrol routing are credible differentiators. However, the deck claims the drones "speak fluent goose" — slide 9 should explain the acoustic-deterrent research behind this rather than leaving it as a punchline.',
    } as any,
    business_model: {
      score: 6,
      maxScore: 10,
      displayName: 'Business Model',
      reasoning:
        '$1,200/month per site SaaS + hardware lease is sensible and the 11 paying golf courses prove willingness to pay. Unit economics are missing: no CAC, no gross margin on the drone fleet, and "geese displaced per dollar" is charming but not a financial metric.',
    } as any,
    market_analysis: {
      score: 5,
      maxScore: 10,
      displayName: 'Market Analysis',
      reasoning:
        "The TAM slide literally multiplies the global goose population (≈6M Canada geese) by an arbitrary $200 'nuisance value.' Bottom-up sizing exists for golf courses (~16,000 in the US) but airports, parks, and corporate campuses are asserted without numbers.",
    } as any,
    gtm_strategy: {
      score: 6,
      maxScore: 10,
      displayName: 'GTM Strategy',
      reasoning:
        'Landing golf-course superintendent associations as a channel is smart and already producing referrals. The plan to expand to airports glosses over FAA/wildlife-control regulation — the single biggest GTM obstacle gets one bullet point.',
    } as any,
    competitive_analysis: {
      score: 5.5,
      maxScore: 10,
      displayName: 'Competitive Analysis',
      reasoning:
        "The competitor matrix lists 'border collies (cannot fly)' and 'plastic owls (geese figured it out in 2019).' Funny, but it omits the two funded drone-deterrence startups and established wildlife-control firms, which investors will know about.",
    } as any,
    management_team: {
      score: 7.5,
      maxScore: 10,
      displayName: 'Management Team',
      reasoning:
        "Strong founder-market fit: CEO was a golf-course superintendent for 9 years ('I have personal beef with geese'), CTO shipped drone-fleet software at a delivery startup. Missing: any advisor with wildlife-regulation or FAA experience.",
    } as any,
    financials: {
      score: 4.5,
      maxScore: 10,
      displayName: 'Financials',
      reasoning:
        'Revenue jumps from $190K ARR to $12M in year 3 with no stated assumptions about site acquisition rate, churn, or fleet costs. The only expense detail is a line item labeled "drone repairs (goose-related)." Investors will need a real model.',
    } as any,
    funding_ask: {
      score: 7,
      maxScore: 10,
      displayName: 'Funding Ask',
      reasoning:
        'Clear ask: $1.5M seed for 18 months of runway, with use of funds split across fleet expansion (40%), engineering (35%), and sales (25%). Milestones are concrete (100 sites, FAA waiver for two airports), which is more than most decks manage.',
    } as any,
  },
  key_strengths: [
    'Real, paying traction: 11 golf courses at $1,200/month with three written referrals',
    'Unforgettable narrative and brand — investors will remember this deck',
    'Genuine founder-market fit (9 years of professional goose adjacency)',
    'Specific, milestone-based funding ask',
  ],
  key_weaknesses: [
    "TAM analysis is built on the global goose population rather than addressable sites",
    'Financial projections have no underlying assumptions',
    'Competitive analysis ignores funded direct competitors',
    'Regulatory path for the airport segment is unaddressed',
  ],
  recommendations: [
    'Rebuild market sizing bottom-up: sites × price, by segment (golf, corporate, airports)',
    'Add a financial model slide with site-growth, churn, and fleet-cost assumptions',
    'Take the competitive matrix seriously: include direct drone competitors and incumbents',
    'Add an advisor or slide addressing FAA and wildlife-control regulations',
    "Keep the humor — but back every joke with a number ('The Boys' cost that office park $40K/year)",
  ],
  recommendations_by_category: {},
  next_steps: [],
};
